import type { Timestamp } from '@bufbuild/protobuf/wkt'

import type { ContactQuoteInteraction } from '@/gen/cortado/v1/invoices_pb'
import { QuoteStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { formatShortDate } from '@/lib/date-utils'

/**
 * How a timeline step reads: `reached` (filled success dot), `pending` (hollow
 * muted dot), `declined` (filled destructive dot) or `expired` (filled muted
 * destructive dot).
 */
export type JobQuoteTimelineTone = 'reached' | 'pending' | 'declined' | 'expired'

export interface JobQuoteTimelineStep {
  key: 'sent' | 'viewed' | 'expires' | 'accepted' | 'declined' | 'expired'
  label: string
  tone: JobQuoteTimelineTone
}

/** The quote's dates the timeline reads. Viewed/accepted/rejected come from the full quote and fill in later. */
export interface JobQuoteTimelineDates {
  sentAt?: Timestamp
  viewedAt?: Timestamp
  acceptedAt?: Timestamp
  rejectedAt?: Timestamp
  validUntil?: Timestamp
  /** How many times the client opened the quote, summed across its recipient links. Above 1 it reads "Viewed 4×". */
  viewCount?: number
  /** The latest view; dates the viewed step when the full quote has no `viewed_at`. */
  lastViewedAt?: Timestamp
}

/** A quote's client views, summed from its public-link interactions. */
export interface JobQuoteViewSummary {
  viewCount: number
  lastViewedAt?: Timestamp
}

function timestampMillis(timestamp: Timestamp | undefined): number {
  return timestamp ? Number(timestamp.seconds) * 1000 + Math.floor(timestamp.nanos / 1_000_000) : 0
}

/**
 * Sums each quote's view count across its interactions (one per recipient
 * link) and keeps the latest view, keyed by quote ID.
 */
export function jobQuoteViewSummaries(interactions: ContactQuoteInteraction[]): Record<string, JobQuoteViewSummary> {
  const summaries: Record<string, JobQuoteViewSummary> = {}
  for (const interaction of interactions) {
    const summary = (summaries[interaction.quoteId] ??= { viewCount: 0 })
    summary.viewCount += Math.max(0, interaction.viewCount)
    if (timestampMillis(interaction.lastViewedAt) > timestampMillis(summary.lastViewedAt)) {
      summary.lastViewedAt = interaction.lastViewedAt
    }
  }
  return summaries
}

/** The muted hint at the right of a tile's footer. */
export type JobQuoteTileHint = { kind: 'text'; text: string } | { kind: 'reminder' }

export interface JobQuoteTileFooter {
  /** The primary link's verb: drafts and unsent revisions are edited, everything else viewed. */
  action: 'Edit' | 'View'
  hint?: JobQuoteTileHint
}

const S = QuoteStatusEnum

/**
 * The status pill's label on a quote tile when it differs from the app-wide
 * one: the tile reads "Sent" (not "Live in portal") and "Declined" (not
 * "Rejected"). Undefined keeps the shared label.
 */
export function jobQuoteTileStatusLabel(status: QuoteStatusEnum): string | undefined {
  if (status === S.QUOTE_STATUS_SENT) return 'Sent'
  if (status === S.QUOTE_STATUS_REJECTED) return 'Declined'
  return undefined
}

function withDate(label: string, timestamp: Timestamp | undefined): string {
  return timestamp ? `${label} ${formatShortDate(timestamp)}` : label
}

/** "Viewed Oct 14", or "Viewed 4× · Oct 14" once the client has opened it more than once. */
function viewedLabel(viewCount: number | undefined, timestamp: Timestamp | undefined): string {
  if (!viewCount || viewCount <= 1) return withDate('Viewed', timestamp)
  const counted = `Viewed ${viewCount}×`
  return timestamp ? `${counted} · ${formatShortDate(timestamp)}` : counted
}

/**
 * A sent quote's history, left to right: Sent, Viewed, then what comes next —
 * the pending expiry while it's out, or the terminal step (Accepted, Declined,
 * Expired). Drafts, cancelled quotes and revisions that were never sent have
 * no timeline. A revision that was sent before shows only what already
 * happened; its expiry reads on the meta line with the draft's.
 */
export function jobQuoteTimeline(status: QuoteStatusEnum, dates: JobQuoteTimelineDates): JobQuoteTimelineStep[] {
  const isOut = status === S.QUOTE_STATUS_SENT || status === S.QUOTE_STATUS_VIEWED
  const isTerminal =
    status === S.QUOTE_STATUS_ACCEPTED || status === S.QUOTE_STATUS_REJECTED || status === S.QUOTE_STATUS_EXPIRED
  const isSentRevision = status === S.QUOTE_STATUS_REVISED && Boolean(dates.sentAt)
  if (!isOut && !isTerminal && !isSentRevision) return []

  const steps: JobQuoteTimelineStep[] = []
  // Out for review implies it was sent, even before the date arrives.
  if (dates.sentAt || isOut) steps.push({ key: 'sent', label: withDate('Sent', dates.sentAt), tone: 'reached' })

  const viewedAt = dates.viewedAt ?? dates.lastViewedAt
  if (viewedAt || (dates.viewCount ?? 0) > 0 || status === S.QUOTE_STATUS_VIEWED) {
    steps.push({ key: 'viewed', label: viewedLabel(dates.viewCount, viewedAt), tone: 'reached' })
  } else if (isOut) {
    steps.push({ key: 'viewed', label: 'Not viewed yet', tone: 'pending' })
  }

  if (isOut && dates.validUntil) {
    steps.push({ key: 'expires', label: withDate('Expires', dates.validUntil), tone: 'pending' })
  } else if (status === S.QUOTE_STATUS_ACCEPTED) {
    steps.push({ key: 'accepted', label: withDate('Accepted', dates.acceptedAt), tone: 'reached' })
  } else if (status === S.QUOTE_STATUS_REJECTED) {
    steps.push({ key: 'declined', label: withDate('Declined', dates.rejectedAt), tone: 'declined' })
  } else if (status === S.QUOTE_STATUS_EXPIRED) {
    steps.push({ key: 'expired', label: withDate('Expired', dates.validUntil), tone: 'expired' })
  }
  return steps
}

/**
 * "Valid until Oct 19" for the meta line of a quote whose timeline doesn't
 * carry its expiry: a draft, or a revision whose changes aren't sent.
 */
export function jobQuoteValidUntilText(status: QuoteStatusEnum, validUntil: Timestamp | undefined): string | undefined {
  if (!validUntil) return undefined
  if (status !== S.QUOTE_STATUS_DRAFT && status !== S.QUOTE_STATUS_REVISED) return undefined
  return `Valid until ${formatShortDate(validUntil)}`
}

/**
 * A tile's footer: the primary link's verb and the muted hint beside it.
 * Sent and viewed quotes offer a reminder (a resend through the send dialog)
 * when `canRemind`; otherwise they read "Waiting on client". A declined quote
 * names its reason category when the client gave one.
 */
export function jobQuoteTileFooter(
  status: QuoteStatusEnum,
  options: { canRemind?: boolean; rejectionLabel?: string } = {},
): JobQuoteTileFooter {
  const text = (value: string): JobQuoteTileHint => ({ kind: 'text', text: value })
  switch (status) {
    case S.QUOTE_STATUS_DRAFT:
      return { action: 'Edit', hint: text('Not visible to client') }
    case S.QUOTE_STATUS_SENT:
    case S.QUOTE_STATUS_VIEWED:
      return { action: 'View', hint: options.canRemind ? { kind: 'reminder' } : text('Waiting on client') }
    case S.QUOTE_STATUS_REVISED:
      return { action: 'Edit', hint: text('Changes not sent') }
    case S.QUOTE_STATUS_REJECTED:
      return { action: 'View', hint: text(options.rejectionLabel || 'Declined by client') }
    case S.QUOTE_STATUS_EXPIRED:
      return { action: 'View', hint: text('Never accepted') }
    case S.QUOTE_STATUS_ACCEPTED:
      return { action: 'View', hint: text('Accepted by client') }
    default:
      return { action: 'View' }
  }
}
